import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Portal } from "@radix-ui/react-portal";
import { skipToken } from "@reduxjs/toolkit/query";
import { EmployeeName } from "components/EmployeeName";
import { FileUpload } from "components/FileUpload";
import { Select, SelectItem } from "components/Select";
import { format, formatISO, startOfDay } from "date-fns";
import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  createLeaveApplication,
  leaveTypeApi,
  selectIsManager,
  selectLeavePeriodEntities,
  updateLeaveApplication,
  useConflictLeaveApplicationsQuery,
  useFetchEmployeesQuery,
  useGetConfigQuery,
  useOverlappingLeaveApplicationsQuery,
} from "store/reducers";

import { LeavePolicy } from "./LeavePolicy";
import { Button } from "../components/button";
import { EmployeeComboBox } from "../components/employeeComboBox";
import { Modal, ModalProps } from "../components/modal";
import { TextArea } from "../components/textarea";

type Props = {
  leave?: LeaveApplication;
  defaults?: {
    employee_id?: number;
    start?: Date;
  };
} & Omit<ModalProps, "title">;

export function LeaveApplicationModal(props: Props) {
  const { leave, defaults, ...rest } = props;

  const { data: configData } = useGetConfigQuery();
  const isManager = useAppSelector(selectIsManager);

  const dispatch = useAppDispatch();

  const [leaveStartDate, setLeaveStartDate] = React.useState<Date>(
    defaults?.start ||
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      (leave?.xero_StartDate && new Date(leave.xero_StartDate)) ||
      startOfDay(new Date()),
  );
  const [leaveEndDate, setLeaveEndDate] = React.useState<Date>(
    defaults?.start ||
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      (leave?.xero_StartDate && new Date(leave.xero_EndDate)) ||
      startOfDay(new Date()),
  );
  const overlappingLeavePeriods = useOverlappingLeaveApplicationsQuery(
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    leaveStartDate && leaveEndDate
      ? {
          start: formatISO(leaveStartDate, { representation: "date" }),
          end: formatISO(leaveEndDate, { representation: "date" }),
        }
      : skipToken,
  );
  const [leaveDatesChanged, setLeaveDatesChanged] = React.useState(false);
  const [leavePeriodsForm, setLeavePeriodsForm] =
    React.useState<Pick<LeavePeriod, "xero_NumberOfUnits" | "xero_PayPeriodEndDate">[]>();
  const leavePeriodEntities = useAppSelector(selectLeavePeriodEntities);
  const [startDatePickerIsOpen, setStartDatePickerIsOpen] = React.useState(false);
  const [endDatePickerIsOpen, setEndDatePickerIsOpen] = React.useState(false);
  const [notes, setNotes] = React.useState(leave?.xero_Title || "");
  const [leaveType, setLeaveType] = React.useState<XeroLeaveType>();
  const [tempAttachments, setTempAttachments] = React.useState<Upload[]>(
    leave && leave.attachment ? [leave.attachment] : [],
  );
  const { data: employees } = useFetchEmployeesQuery(undefined);
  const [hasLeaveConflict, setHasLeaveConflict] = React.useState<boolean>(false);

  // While it is null we don't render the Select
  const [selectedEmployee, setSelectedEmployee] = React.useState<Employee | null | undefined>(null);
  const xero_tenantId = selectedEmployee?.xero_tenantId;
  const leaveTypesQuery = leaveTypeApi.useGetLeaveTypesQuery(xero_tenantId != null ? { xero_tenantId } : skipToken);

  const conflictLeavePeriods = useConflictLeaveApplicationsQuery({
    start: formatISO(leaveStartDate, { representation: "date" }),
    end: formatISO(leaveEndDate, { representation: "date" }),
  });

  React.useEffect(() => {
    function getDefaultEmployee() {
      if (!employees) return null;
      if (leave) {
        let employee = employees.employees.find((e: Employee) => e.id === leave.employee_id);
        return employee; // should not be undefined unless there's a bug and the employee wasn't loaded
      }
      if (defaults?.employee_id) {
        let employee = employees.employees.find((e: Employee) => e.id === defaults.employee_id);
        if (employee) return employee;
      }
      // If no default employee, return the current employee
      return configData?.currentEmployee;
    }
    setSelectedEmployee(getDefaultEmployee());
  }, [employees, configData, leave, defaults]);

  // Initialise leave periods for new leave request
  React.useEffect(() => {
    if (
      (!leave || leaveDatesChanged) &&
      // When leave dates change, it fetches the new data. Make sure it's done fetching so it doesn't use the old data.
      !overlappingLeavePeriods.isFetching &&
      overlappingLeavePeriods.data
    ) {
      setLeavePeriodsForm(
        overlappingLeavePeriods.data.periods.map((p) => ({
          ...p,
          xero_PayPeriodEndDate: p.end,
          xero_NumberOfUnits: p.units,
        })),
      );
      setLeaveDatesChanged(false);
    }
  }, [leave, overlappingLeavePeriods, leaveDatesChanged]);

  // Initialise leave periods from existing leave request
  React.useEffect(() => {
    if (leave) {
      const leavePeriods = leave.leave_period_ids
        .map((id) => leavePeriodEntities[id])
        .filter((p): p is LeavePeriod => !!p);
      setLeavePeriodsForm(
        leavePeriods.map((p) => ({
          ...p,
        })),
      );
    }
  }, [leaveTypesQuery.data]);

  React.useEffect(() => {
    const { isFetching, data } = conflictLeavePeriods;

    if (!isFetching && data) {
      const leaveApplications = data.leave_applications || [];
      if (leaveApplications.length > 0) {
        setHasLeaveConflict(true);
      } else {
        setHasLeaveConflict(false);
      }
    }
  }, [conflictLeavePeriods, hasLeaveConflict]);

  function toggleStartDatePicker() {
    setStartDatePickerIsOpen(!startDatePickerIsOpen);
  }

  function toggleEndDatePicker() {
    setEndDatePickerIsOpen(!endDatePickerIsOpen);
  }

  function onChangeStartDate(date: Date | null) {
    if (!date) {
      return;
    }
    setLeaveStartDate(date);
    if (leaveEndDate < date) {
      setLeaveEndDate(date);
    }
    setLeaveDatesChanged(true);
  }

  function onChangeEndDate(date: Date | null) {
    if (!date) {
      return;
    }
    setLeaveEndDate(date);
    if (leaveStartDate > date) {
      setLeaveStartDate(date);
    }
    setLeaveDatesChanged(true);
  }
  function onChangeLeaveAmount(e: any, index: number) {
    setLeavePeriodsForm(
      leavePeriodsForm?.map((data, i) => {
        if (index === i) {
          return {
            ...data,
            xero_NumberOfUnits: e.target.value,
          };
        }
        return data;
      }),
    );
  }

  function onChangeNotes(e: any) {
    setNotes(e.target.value);
  }

  function onChangeLeaveType(leaveTypeId: string) {
    let leaveType = leaveTypesQuery.data?.LeaveTypes.find((type) => type.leave_type_id === leaveTypeId);
    setLeaveType(leaveType);
  }

  async function onClickSubmit() {
    if (!configData || !leaveType || !selectedEmployee) {
      throw new Error("Missing data");
    }
    if (!overlappingLeavePeriods.isLoading) {
      let updateObject = {
        xero_LeaveTypeID: leaveType.leave_type_id,
        xero_LeaveTypeName: leaveType.name,
        xero_Title: notes === "" ? undefined : notes,
        xero_StartDate: formatISO(leaveStartDate, {
          representation: "date",
        }),
        xero_EndDate: formatISO(leaveEndDate, { representation: "date" }),
        leave_periods: leavePeriodsForm?.map((data) => ({
          xero_PayPeriodEndDate: data.xero_PayPeriodEndDate,
          xero_NumberOfUnits: data.xero_NumberOfUnits,
        })),
        attachment_filename: tempAttachments[0]?.filename ?? null,
      };

      if (leave) {
        await dispatch(
          updateLeaveApplication({
            leaveApplicationId: leave.id,
            body: updateObject as Partial<LeaveApplication>,
          }),
        );
        props.dismissModal();
      } else {
        await dispatch(
          createLeaveApplication({
            ...updateObject,
            employee_id: selectedEmployee.id,
          }),
        );
        props.dismissModal();
      }
    }
  }

  React.useEffect(() => {
    if (leaveTypesQuery.data) {
      if (leave) {
        // Initialise from the existing leave request
        setLeaveType(
          leaveTypesQuery.data.LeaveTypes.find((leaveType) => {
            return leaveType.leave_type_id === leave.xero_LeaveTypeID;
          }),
        );
      } else {
        // Select the first leave type by default (should be Annual Leave in Xero's response)
        setLeaveType(leaveTypesQuery.data.LeaveTypes[0]);
      }
    }
  }, [leaveTypesQuery.data, leave]);

  return (
    <Modal
      title={leave ? "Edit Leave Request" : isManager ? "Create a Leave Request" : "Request Leave"}
      contentLabel="Apply for Leave Modal"
      buttons={
        <Button onClick={onClickSubmit} colour="accent" disabled={overlappingLeavePeriods.isLoading}>
          Submit
        </Button>
      }
      {...rest}
    >
      <div className="flex flex-col">
        {isManager && (
          <div className="md:flex mb-6">
            <div className="md:w-1/2">
              {!leave && employees && selectedEmployee !== null && (
                <EmployeeComboBox
                  defaultValue={selectedEmployee}
                  employees={employees.employees}
                  onSelectEmployee={(employee) => setSelectedEmployee(employee)}
                />
              )}
              {leave && (
                <div>
                  <label className="block uppercase tracking-wide text-grey-darker text-xs font-bold mb-2">
                    Employee Name
                  </label>
                  <div className="flex">
                    <span className="text-primary-dark">
                      {selectedEmployee && <EmployeeName employeeId={selectedEmployee.id} />}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="md:flex mb-6">
          <div className="md:w-full">
            <label className="block uppercase tracking-wide text-grey-darker text-xs font-bold mb-2">Leave Type</label>
            {leaveTypesQuery.isLoading && <div>Loading...</div>}
            {leaveTypesQuery.isError && <div>Error</div>}
            {/* leaveType checked first because otherwise it doesn't bind to the new value when changing from undefined */}
            {leaveType && leaveTypesQuery.isSuccess && (
              <Select onValueChange={onChangeLeaveType} value={leaveType.leave_type_id}>
                {leaveTypesQuery.data.LeaveTypes.map((leaveType) => {
                  return (
                    <SelectItem value={leaveType.leave_type_id} key={leaveType.leave_type_id}>
                      {leaveType.name}
                    </SelectItem>
                  );
                })}
              </Select>
            )}
          </div>
        </div>
        {hasLeaveConflict && (
          <div className="text-danger my-2">
            <FontAwesomeIcon icon="exclamation-circle" className="text-accent" />
            <span>
              There are other leave requests for the same period. Please note that your leave request will need approval
              if submitted.
            </span>
          </div>
        )}

        <div className="md:flex mb-6">
          <div className="md:w-1/2 mb-3 md:mb-0 mr-3">
            <label className="block uppercase tracking-wide text-grey-darker text-xs font-bold mb-2">
              First day of leave
            </label>
            <div className="flex">
              <DatePicker
                className={
                  "appearance-none block w-full text-grey-darker border border-primary-light rounded-l py-3 px-4 mb-3 focus:outline-none"
                }
                selected={leaveStartDate}
                onChange={onChangeStartDate}
                open={startDatePickerIsOpen}
                onSelect={toggleStartDatePicker}
                dateFormat="dd/MM/yyyy"
                // Append to document.body
                popperContainer={Portal}
                disabledKeyboardNavigation
              />
              <Button
                onClick={toggleStartDatePicker}
                colour="accent"
                className="mb-3 rounded-r rounded-l-none py-2 px-4"
              >
                <FontAwesomeIcon icon="calendar-alt" />
              </Button>
            </div>
          </div>
          <div className="md:w-1/2">
            <label className="block uppercase tracking-wide text-grey-darker text-xs font-bold mb-2">
              Last day of leave
            </label>
            <div className="flex">
              <DatePicker
                className={
                  "appearance-none block w-full text-grey-darker border border-primary-light rounded-l py-3 px-4 mb-3 focus:outline-none"
                }
                selected={leaveEndDate}
                onChange={onChangeEndDate}
                open={endDatePickerIsOpen}
                onSelect={toggleEndDatePicker}
                dateFormat="dd/MM/yyyy"
                popperContainer={Portal}
                disabledKeyboardNavigation
              />
              <Button onClick={toggleEndDatePicker} colour="accent" className="mb-3 rounded-r rounded-l-none py-2 px-4">
                <FontAwesomeIcon icon="calendar-alt" />
              </Button>
            </div>
          </div>
        </div>

        <div className="md:flex mb-6">
          <div className="md:w-1/2 mb-6 md:mb-0">
            <label className="block uppercase tracking-wide text-grey-darker text-xs font-bold mb-2">
              Leave Required
            </label>
            {leavePeriodsForm?.map((period, i) => {
              return (
                <div key={i}>
                  <div>In Pay Period ending {format(new Date(period.xero_PayPeriodEndDate), "dd/MM/yyyy")}</div>
                  <div className="flex mb-3">
                    <input
                      type="number"
                      className={
                        "appearance-none block w-full bg-grey-lighter text-grey-darker border border-primary-light rounded-l py-3 px-4 focus:outline-none focus:ring"
                      }
                      value={period.xero_NumberOfUnits}
                      onChange={(e) => onChangeLeaveAmount(e, i)}
                    />
                    <div className="bg-accent text-white font-bold py-2 px-4 rounded-r focus:outline-none items-center flex">
                      <span className="flex whitespace-nowrap">work hours</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {/* <div className="md:w-1/2 px-3">
                        <label className="block uppercase tracking-wide text-grey-darker text-xs font-bold mb-2">
                          Available balance
                        </label>
                        <div>
                          <input
                            className={
                              "appearance-none block w-full bg-grey-lighter text-grey-darker border border-primary-light rounded py-3 px-4 mb-3 focus:outline-none cursor-not-allowed bg-background"
                            }
                            disabled
                          ></input>
                        </div>
                      </div> */}
        </div>

        <div className="md:flex mb-2">
          <div className="md:w-full">
            <label className="block uppercase tracking-wide text-grey-darker text-xs font-bold mb-2">Notes</label>
            <TextArea
              name="Notes"
              placeholder="Message..."
              value={notes}
              onChange={(e) => onChangeNotes(e)}
              maxLength={100}
            />
          </div>
        </div>

        <div className="md:flex mb-2">
          <div className="md:w-full">
            <label className="block uppercase tracking-wide text-grey-darker text-xs font-bold mb-2">Attachments</label>
            <FileUpload
              attachments={tempAttachments}
              setAttachments={setTempAttachments}
              multiple={false}
              selectedEmployee={selectedEmployee}
            />
          </div>
        </div>

        {!leave && <LeavePolicy />}
      </div>
    </Modal>
  );
}
